import { Link } from '@inertiajs/react';

const layoutStyles = {
    portrait: {
        article:
            'overflow-hidden rounded-[1.75rem] border border-rose-100 bg-white shadow-[0_18px_44px_rgba(236,72,153,0.1)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(236,72,153,0.14)]',
        link: 'block',
        mediaWrap: 'relative overflow-hidden bg-[linear-gradient(180deg,#fff0f5_0%,#fff8fb_55%,#ffffff_100%)] p-4',
        imageWrap:
            'relative mt-10 overflow-hidden rounded-[1.5rem] bg-[radial-gradient(circle_at_50%_20%,#fff_0%,#ffe1ea_38%,#f6b0c7_70%,#e05d88_120%)]',
        image: 'aspect-[4/5] w-full object-cover transition duration-700 group-hover:scale-[1.04]',
        content: 'space-y-3 px-5 py-5',
        title: 'line-clamp-2 text-base font-black leading-tight text-slate-950',
        price: 'text-lg font-black tracking-[-0.04em] text-slate-950',
        footer:
            'grid grid-cols-2 gap-2 px-5 pb-5',
        action:
            'inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-xs font-black uppercase tracking-[0.16em] transition',
    },
    landscape: {
        article:
            'overflow-hidden rounded-[1.9rem] border border-rose-100 bg-white shadow-[0_18px_44px_rgba(236,72,153,0.12)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(236,72,153,0.16)]',
        link: 'block',
        mediaWrap:
            'relative overflow-hidden bg-[linear-gradient(180deg,#ffe7f0_0%,#fff7fb_48%,#ffffff_100%)] p-4 sm:p-5',
        imageWrap:
            'relative mt-10 overflow-hidden rounded-[1.65rem] border border-white/70 bg-[radial-gradient(circle_at_50%_20%,#fff_0%,#fff3f7_33%,#f7d6e3_60%,#f51b75_120%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]',
        image: 'relative z-10 mx-auto aspect-[4/5] w-full max-w-[250px] object-cover object-center transition duration-700 group-hover:scale-[1.04]',
        content: 'space-y-4 px-5 py-5',
        title: 'line-clamp-2 text-[1.05rem] font-black leading-tight text-slate-900 sm:text-[1.15rem]',
        price: 'text-2xl font-black tracking-[-0.04em] text-slate-950',
        footer:
            'grid grid-cols-2 gap-2',
        action:
            'inline-flex items-center justify-center rounded-full px-4 py-3 text-xs font-black uppercase tracking-[0.18em] transition',
    },
};

export function ProductCard({
    product,
    layout = 'portrait',
    badgeLabel,
    statusLabel,
    subtitle,
    priceLabel = 'Starting from',
    primaryActionLabel = 'View Product',
    secondaryActionLabel = 'Quick Look',
    footerActions = null,
    href,
    className = '',
    imageClassName = '',
    emptyStateLabel = 'Preview coming soon',
    emptyStateEyebrow = 'Beauty product',
}) {
    const safeProduct = product || {};
    const resolvedLayout = layoutStyles[layout] ? layout : 'portrait';
    const styles = layoutStyles[resolvedLayout];
    const title = safeProduct.title || safeProduct.name || 'Product';
    const image =
        safeProduct.image ||
        safeProduct.hoverImage ||
        safeProduct.gallery?.[0]?.src ||
        safeProduct.images?.[0]?.src ||
        safeProduct.images?.[0] ||
        '';
    const price = Number(safeProduct.price || 0);
    const productHref = href || (safeProduct.slug ? `/products/${safeProduct.slug}` : '/shop');
    const resolvedSubtitle = subtitle || safeProduct.brand || safeProduct.category || 'Beauty essential';
    const resolvedBadgeLabel =
        badgeLabel ||
        (safeProduct.isSoldOut ? 'Sold out' : safeProduct.badge || safeProduct.badges?.[0] || 'New');
    const resolvedStatusLabel =
        statusLabel || (safeProduct.isSoldOut ? 'Sold out' : 'Ready now');

    return (
        <article className={`group ${styles.article} ${className}`.trim()}>
            <Link href={productHref} className={styles.link}>
                <div className={styles.mediaWrap}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/92 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#d80b67] shadow-sm ring-1 ring-rose-100 backdrop-blur">
                            <span className="size-1.5 rounded-full bg-[#f51b75]" />
                            {resolvedBadgeLabel}
                        </div>

                        <div className="rounded-full bg-[#111827] px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-black/10">
                            {resolvedStatusLabel}
                        </div>
                    </div>

                    <div className={styles.imageWrap}>
                        {image ? (
                            <img
                                src={image}
                                alt={title}
                                className={`${styles.image} ${imageClassName}`.trim()}
                            />
                        ) : (
                            <div className="relative z-10 flex aspect-[4/5] w-full items-center justify-center px-4 text-center">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.28em] text-white/80">
                                        {emptyStateEyebrow}
                                    </p>
                                    <p className="mt-3 text-lg font-black uppercase text-white">
                                        {emptyStateLabel}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.content}>
                    <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#d80b67]/70">
                            {resolvedSubtitle}
                        </p>
                        <h3 className={styles.title}>{title}</h3>
                    </div>

                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">
                                {priceLabel}
                            </p>
                            <span className={`mt-1 block ${styles.price}`}>
                                ৳{price.toLocaleString('en-BD')}
                            </span>
                        </div>

                        <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] ${
                                safeProduct.isSoldOut
                                    ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-100'
                                    : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                            }`}
                        >
                            {resolvedStatusLabel}
                        </span>
                    </div>

                    {resolvedLayout === 'portrait' && !footerActions ? (
                        <div className={styles.footer}>
                            <span
                                className={`${styles.action} bg-[#f51b75] text-white group-hover:bg-[#d80b67]`}
                            >
                                {primaryActionLabel}
                            </span>
                            <span
                                className={`${styles.action} border border-rose-200 bg-white text-[#d80b67] group-hover:border-[#f51b75] group-hover:bg-[#fff7fa]`}
                            >
                                {secondaryActionLabel}
                            </span>
                        </div>
                    ) : null}
                </div>
            </Link>

            {footerActions ? <div className="px-5 pb-5">{footerActions}</div> : null}
        </article>
    );
}
